import { ColumnType } from '../../../../grid/types/common';

export enum ColumnIndexes {
  PRODUCT_ID,
  PRODUCT_TYPE,
  PROTECTION_MODEL,
  PRODUCT_STATUS,
  LICENSE_KEY,
  TOTAL_SEATS,
  RESERVED_SEATS,
  USED_RESERVED_SEATS,
  AVAILABLE_RESERVED_SEATS,
  UNRESERVED_SEATS,
  USED_UNRESERVED_SEATS,
  AVAILABLE_UNRESERVED_SEATS,
  MINIMAL_COMMITMENT,
  SUBSCRIPTION_END_DATE,
  AUTO_RENEWAL,
  EXPIRY_DATE,
  USAGE_BREAKDOWN,
}

export enum ColumnKeys {
  PRODUCT_ID = 'productId',
  PRODUCT_TYPE = 'productType',
  PROTECTION_MODEL = 'protectionModel',
  PRODUCT_STATUS = 'productStatus',
  LICENSE_KEY = 'licenseKey',
  TOTAL_SEATS = 'totalSeats',
  RESERVED_SEATS = 'reservedSeats',
  USED_RESERVED_SEATS = 'usedReservedSeats',
  AVAILABLE_RESERVED_SEATS = 'availableReservedSeats',
  UNRESERVED_SEATS = 'unreservedSeats',
  USED_UNRESERVED_SEATS = 'usedUnreservedSeats',
  AVAILABLE_UNRESERVED_SEATS = 'availableUnreservedSeats',
  MINIMAL_COMMITMENT = 'minimalCommitment',
  SUBSCRIPTION_END_DATE = 'subscriptionEndDate',
  AUTO_RENEWAL = 'autoRenewal',
  EXPIRY_DATE = 'expiryDate',
  USAGE_BREAKDOWN = 'usageBreakdown',
}

export const PAGE_SIZE = 50;

export const DEFAULT_COLUMN_VISIBILITY_FOR_MONTHLY = {
  [ColumnKeys.PRODUCT_ID]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_ID,
  },
  [ColumnKeys.PRODUCT_TYPE]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_TYPE,
  },
  [ColumnKeys.PROTECTION_MODEL]: {
    isVisible: true,
    index: ColumnIndexes.PROTECTION_MODEL,
  },
  [ColumnKeys.PRODUCT_STATUS]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_STATUS,
  },
  [ColumnKeys.LICENSE_KEY]: {
    isVisible: false,
    index: ColumnIndexes.LICENSE_KEY,
  },
  [ColumnKeys.TOTAL_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.TOTAL_SEATS,
  },
  [ColumnKeys.RESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.RESERVED_SEATS,
  },
  [ColumnKeys.USED_RESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.USED_RESERVED_SEATS,
  },
  [ColumnKeys.AVAILABLE_RESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.AVAILABLE_RESERVED_SEATS,
  },
  [ColumnKeys.UNRESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.UNRESERVED_SEATS,
  },
  [ColumnKeys.USED_UNRESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.USED_UNRESERVED_SEATS,
  },
  [ColumnKeys.AVAILABLE_UNRESERVED_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.AVAILABLE_UNRESERVED_SEATS,
  },
  [ColumnKeys.MINIMAL_COMMITMENT]: {
    isVisible: false,
    index: ColumnIndexes.MINIMAL_COMMITMENT,
  },
  [ColumnKeys.SUBSCRIPTION_END_DATE]: {
    isVisible: false,
    index: ColumnIndexes.SUBSCRIPTION_END_DATE,
  },
  [ColumnKeys.AUTO_RENEWAL]: {
    isVisible: false,
    index: ColumnIndexes.AUTO_RENEWAL,
  },
  [ColumnKeys.EXPIRY_DATE]: {
    isVisible: false,
    index: ColumnIndexes.EXPIRY_DATE,
  },
  [ColumnKeys.USAGE_BREAKDOWN]: {
    isVisible: false,
    index: ColumnIndexes.USAGE_BREAKDOWN,
  },
};

export const DEFAULT_COLUMN_VISIBILITY_FOR_YEARLY = {
  [ColumnKeys.PRODUCT_ID]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_ID,
  },
  [ColumnKeys.PRODUCT_TYPE]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_TYPE,
  },
  [ColumnKeys.PRODUCT_STATUS]: {
    isVisible: true,
    index: ColumnIndexes.PRODUCT_STATUS,
  },
  [ColumnKeys.LICENSE_KEY]: {
    isVisible: true,
    index: ColumnIndexes.LICENSE_KEY,
  },
  [ColumnKeys.EXPIRY_DATE]: {
    isVisible: true,
    index: ColumnIndexes.EXPIRY_DATE,
  },
  [ColumnKeys.TOTAL_SEATS]: {
    isVisible: true,
    index: ColumnIndexes.TOTAL_SEATS,
  },
  [ColumnKeys.USAGE_BREAKDOWN]: {
    isVisible: true,
    index: ColumnIndexes.USAGE_BREAKDOWN,
  },
};

export interface ColumnValue {
  label: string;
  value: number;
}

export interface ColumnDetails {
  column: ColumnIndexes;
  key: ColumnKeys;
  columnName: string;
  type: ColumnType;
  values?: Array<ColumnValue>;
  removable: boolean;
}

export function getColumnDetails(translateFn) {
  return [
    {
      column: ColumnIndexes.PRODUCT_ID,
      key: ColumnKeys.PRODUCT_ID,
      columnName: translateFn('Product name'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.PRODUCT_TYPE,
      key: ColumnKeys.PRODUCT_TYPE,
      columnName: translateFn('Product type'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.PROTECTION_MODEL,
      key: ColumnKeys.PROTECTION_MODEL,
      columnName: translateFn('Protection model'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.PRODUCT_STATUS,
      key: ColumnKeys.PRODUCT_STATUS,
      columnName: translateFn('Product status'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.LICENSE_KEY,
      key: ColumnKeys.LICENSE_KEY,
      columnName: translateFn('License key'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.TOTAL_SEATS,
      key: ColumnKeys.TOTAL_SEATS,
      columnName: translateFn('Total seats'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.RESERVED_SEATS,
      key: ColumnKeys.RESERVED_SEATS,
      columnName: translateFn('Reserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.USED_RESERVED_SEATS,
      key: ColumnKeys.USED_RESERVED_SEATS,
      columnName: translateFn('Used reserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.AVAILABLE_RESERVED_SEATS,
      key: ColumnKeys.AVAILABLE_RESERVED_SEATS,
      columnName: translateFn('Available reserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.UNRESERVED_SEATS,
      key: ColumnKeys.UNRESERVED_SEATS,
      columnName: translateFn('Unreserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.USED_UNRESERVED_SEATS,
      key: ColumnKeys.USED_UNRESERVED_SEATS,
      columnName: translateFn('Used unreserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.AVAILABLE_UNRESERVED_SEATS,
      key: ColumnKeys.AVAILABLE_UNRESERVED_SEATS,
      columnName: translateFn('Available unreserved seats'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.MINIMAL_COMMITMENT,
      key: ColumnKeys.MINIMAL_COMMITMENT,
      columnName: translateFn('Minimum usage'),
      type: ColumnType.NUMBER,
      removable: true,
    },
    {
      column: ColumnIndexes.SUBSCRIPTION_END_DATE,
      key: ColumnKeys.SUBSCRIPTION_END_DATE,
      columnName: translateFn('Subscription end date'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.AUTO_RENEWAL,
      key: ColumnKeys.AUTO_RENEWAL,
      columnName: translateFn('Auto-renewal'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.EXPIRY_DATE,
      key: ColumnKeys.EXPIRY_DATE,
      columnName: translateFn('Expiry date'),
      type: ColumnType.STRING,
      removable: true,
    },
    {
      column: ColumnIndexes.USAGE_BREAKDOWN,
      key: ColumnKeys.USAGE_BREAKDOWN,
      columnName: translateFn('Usage breakdown'),
      type: ColumnType.STRING,
      removable: true,
    },
  ];
}
